import saveAs from 'file-saver'
import ExcelJS from 'exceljs'
import moment from 'moment-timezone'
import {
  dateTime as SharedDateTime,
  constants,
  getPackageFlatServiceFee,
  isPayrollDeductionEnabled,
  resolvePrintedOfferInfo,
} from '@blocktickets/shared-utils'

const { ORDER_SOURCE, PRODUCT_TYPE } = constants

// default headers: report name, organization name, generated at, empty row, table columns
export const DEFAULTNUMHEADERS = 5

// Styling for column cells in Excel spreadsheets
export const excelHeaderStyle = {
  font: { bold: true, color: { argb: 'FFFFFFFF' } },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF3E8BF7' },
  },
  alignment: { horizontal: 'center', vertical: 'middle' },
  border: {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
  },
}

// Styling for row cells in Excel spreadsheets
// wrapText: true to wrap on multiple lines eg. multiple sections, rows, seats
const excelCellStyle = {
  alignment: { horizontal: 'left', vertical: 'top', wrapText: true },
  border: {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
  },
}

// Styling for total sub group row cells in Excel spreadsheets
const excelTotalSubGroupCellStyle = {
  font: { bold: true },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFECF7FE' },
  },
  alignment: { horizontal: 'left', vertical: 'top' },
  border: {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
  },
}

// Styling for total row cells in Excel spreadsheets
const excelTotalCellStyle = {
  font: { bold: true },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFBCE2FC' },
  },
  alignment: { horizontal: 'left', vertical: 'top' },
  border: {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
  },
}

// Styling for empty row cells in Excel spreadsheets
const excelEmptyCellStyle = {
  font: { bold: true },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFECF7FE' },
  },
  alignment: { horizontal: 'left', vertical: 'top' },
}

// global state options
export const canadaStateOpt = [
  {
    name: 'Alberta',
    value: 'AB',
  },
  {
    name: 'British Columbia',
    value: 'BC',
  },
  {
    name: 'Manitoba',
    value: 'MB',
  },
  {
    name: 'New Brunswick',
    value: 'NB',
  },
  {
    name: 'Newfoundland and Labrador',
    value: 'NL',
  },
  {
    name: 'Northwest Territories',
    value: 'NT',
  },
  {
    name: 'Nova Scotia',
    value: 'NS',
  },
  {
    name: 'Nunavut',
    value: 'NU',
  },
  {
    name: 'Ontario',
    value: 'ON',
  },
  {
    name: 'Prince Edward Island',
    value: 'PE',
  },
  {
    name: 'Quebec',
    value: 'QC',
  },
  {
    name: 'Saskatchewan',
    value: 'SK',
  },
  {
    name: 'Yukon Territory',
    value: 'YT',
  },
]

export const stateOpt = [
  { value: 'AK', name: 'Alaska' },
  { value: 'TX', name: 'Texas' },
  { value: 'AL', name: 'Alabama' },
  { value: 'AR', name: 'Arkansas' },
  { value: 'AZ', name: 'Arizona' },
  { value: 'CA', name: 'California' },
  { value: 'CO', name: 'Colorado' },
  { value: 'CT', name: 'Connecticut' },
  { value: 'DC', name: 'District Of Columbia' },
  { value: 'DE', name: 'Delaware' },
  { value: 'FL', name: 'Florida' },
  { value: 'GA', name: 'Georgia' },
  { value: 'HI', name: 'Hawaii' },
  { value: 'IA', name: 'Iowa' },
  { value: 'ID', name: 'Idaho' },
  { value: 'IL', name: 'Illinois' },
  { value: 'IN', name: 'Indiana' },
  { value: 'KS', name: 'Kansas' },
  { value: 'KY', name: 'Kentucky' },
  { value: 'LA', name: 'Louisiana' },
  { value: 'MA', name: 'Massachusetts' },
  { value: 'MD', name: 'Maryland' },
  { value: 'ME', name: 'Maine' },
  { value: 'MI', name: 'Michigan' },
  { value: 'MN', name: 'Minnesota' },
  { value: 'MO', name: 'Missouri' },
  { value: 'MS', name: 'Mississippi' },
  { value: 'MT', name: 'Montana' },
  { value: 'NC', name: 'North Carolina' },
  { value: 'ND', name: 'North Dakota' },
  { value: 'NE', name: 'Nebraska' },
  { value: 'NH', name: 'New Hampshire' },
  { value: 'NJ', name: 'New Jersey' },
  { value: 'NM', name: 'New Mexico' },
  { value: 'NV', name: 'Nevada' },
  { value: 'NY', name: 'New York' },
  { value: 'OH', name: 'Ohio' },
  { value: 'OK', name: 'Oklahoma' },
  { value: 'OR', name: 'Oregon' },
  { value: 'PA', name: 'Pennsylvania' },
  { value: 'RI', name: 'Rhode Island' },
  { value: 'SC', name: 'South Carolina' },
  { value: 'SD', name: 'South Dakota' },
  { value: 'TN', name: 'Tennessee' },
  { value: 'TX', name: 'Texas' },
  { value: 'UT', name: 'Utah' },
  { value: 'VA', name: 'Virginia' },
  { value: 'VT', name: 'Vermont' },
  { value: 'WA', name: 'Washington' },
  { value: 'WI', name: 'Wisconsin' },
  { value: 'WV', name: 'West Virginia' },
  { value: 'WY', name: 'Wyoming' },
]

// event/offers/packages/flex pack statuses
export const globalStatuses = {
  unpublished: {
    type: 'unpublished',
    label: 'Draft',
  },
  draft: {
    type: 'draft',
    label: 'Draft',
  },
  scheduled: {
    type: 'scheduled',
    label: 'Scheduled',
  },
  publishing: {
    type: 'publishing',
    label: 'Publishing',
  },
  published: {
    type: 'published',
    label: 'Published',
  },
  on_sale: {
    type: 'onsale',
    label: 'On Sale',
  },
  complete: {
    type: 'ended',
    label: 'Ended',
  },
  hidden: {
    type: 'hidden',
    label: 'Hidden',
  },
  sold_out: {
    type: 'soldout',
    label: 'Sold Out',
  },
  cancelled: {
    type: 'cancelled',
    label: 'Cancelled',
  },
}

// options for event/packages/flex pack status filter
export const globalStatusOpts = [
  {
    label: 'Upcoming',
    value: 'upcoming',
  },
  {
    label: 'Past',
    value: 'complete',
  },
  {
    label: 'Draft',
    value: 'draft',
  },
  {
    label: 'Hidden',
    value: 'hidden',
  },
]

export const paymentStatuses = {
  complete: {
    label: 'Complete',
    longLabel: 'Complete',
    value: 'complete',
  },
  draft: {
    label: 'Pending',
    longLabel: 'Pending',
    value: 'pending',
  },
  open: {
    label: 'Open',
    longLabel: 'Open',
    value: 'open',
  },
  paid: {
    label: 'Paid',
    longLabel: 'Paid Online',
    value: 'paid',
  },
  plan: {
    label: 'Payment plan',
    longLabel: 'Payment Plan',
    value: 'plan',
  },
  cash: {
    label: 'Cash',
    longLabel: 'Paid In Cash',
    value: 'cash',
  },
  overdue: {
    label: 'Overdue',
    longLabel: 'Overdue',
    value: 'overdue',
  },
  past_due: {
    label: 'Past Due',
    longLabel: 'Order Past Due',
    value: 'past_due',
  },
  refunded: {
    label: 'Refunded',
    longLabel: 'Refunded',
    value: 'refunded',
  },
  released: {
    label: 'Released',
    longLabel: 'Released',
    value: 'released',
  },
  partially_refunded: {
    label: 'Partial refund',
    longLabel: 'Partially Refunded',
    value: 'partially_refunded',
  },
  reserved: {
    label: 'Reserved',
    longLabel: 'Reserved',
    value: 'reserved',
  },
  comp: {
    label: 'Comp',
    longLabel: 'Comp',
    value: 'comp',
  },
}

export function parseOrderDetails(details) {
  if (!details) return {}
  if (typeof details === 'string') {
    try {
      return JSON.parse(details)
    } catch {
      return {}
    }
  }
  return details
}

export function isOrderPartiallyRefunded(order) {
  const status = String(order?.status || '').toLowerCase()
  if (status === 'refunded' || status === 'released') return false
  if (
    order?.partiallyRefunded === true ||
    order?.partiallyRefunded === 'true'
  ) {
    return true
  }
  const details = parseOrderDetails(order?.details)
  if (details?.partiallyRefunded) return true
  return getOrderRefundAmount(order) > 0
}

export function getOrderRefundStatusLabel(order) {
  const status = String(order?.status || '').toLowerCase()
  if (status === 'refunded') return 'Refunded'
  if (isOrderPartiallyRefunded(order)) return 'Partially Refunded'
  return null
}

export function getOrderTotalRefundedAmount(order) {
  const details = parseOrderDetails(order?.details)
  return Number(details?.totalRefundedAmount || 0)
}

export function getOrderRefundAmount(order) {
  const fromListing = Number(order?.refundAmount)
  if (Number.isFinite(fromListing) && fromListing > 0) return fromListing

  const status = String(order?.status || '').toLowerCase()
  if (status === 'refunded') {
    return Number(order?.total ?? order?.gross ?? order?.net ?? 0)
  }

  return getOrderTotalRefundedAmount(order)
}

export function getOrderLastRefundDate(order) {
  return (
    order?.lastRefundAt ||
    parseOrderDetails(order?.details)?.lastPartialRefundAt ||
    null
  )
}

export const orderStatuses = {
  open: {
    label: 'Open',
    value: 'open',
  },
  incomplete: {
    label: 'Incomplete',
    value: 'incomplete',
  },
  failed: {
    label: 'Failed',
    value: 'failed',
  },
  complete: {
    label: 'Complete',
    longLabel: 'Complete',
    value: 'complete',
  },
  completeFromExchange: {
    label: 'Complete',
    value: 'complete',
  },
  completeFromTransfer: {
    label: 'Complete',
    value: 'complete',
  },
  completeFromGroupPurchase: {
    label: 'Complete',
    longLabel: 'Complete',
    value: 'complete',
  },
  completeFromInvoice: {
    label: 'Complete',
    longLabel: 'Complete',
    value: 'complete',
  },
  completeFromAssignment: {
    label: 'Complete',
    longLabel: 'Complete',
    value: 'complete',
  },
  past_due: {
    label: 'Past due',
    longLabel: 'Order Past Due',
    value: 'past_due',
  },
  pending_send: {
    label: 'Pending Send',
    value: 'pending_send',
  },
  refunded: {
    label: 'Refunded',
    longLabel: 'Refunded',
    value: 'refunded',
  },
  released: {
    label: 'Released',
    longLabel: 'Released',
    value: 'released',
  },
  reserved: {
    label: 'Reserved',
    longLabel: 'Reserved',
    value: 'reserved',
  },
  comp: {
    label: 'Comp',
    longLabel: 'Comp',
    value: 'comp',
  },
}

export const typeStatuses = {
  primary: {
    label: 'Primary',
    value: 'primary',
  },
  resale: {
    label: 'Resale',
    value: 'resale',
  },
  transfer: {
    label: 'Transfer',
    value: 'transfer',
  },
  groupPurchase: {
    label: 'Ticket assignment',
    value: 'ticket_assignment',
  },
  package: {
    label: 'Ticket assignment',
    value: 'ticket_assignment',
  },
  box_office: {
    label: 'Box office',
    value: 'box_office',
  },
}

export const sourceStatuses = {
  ticket_assignment: {
    label: 'Ticket assignment',
    value: 'ticket_assignment',
  },
  ticket_exchange: {
    label: 'Ticket exchange',
    value: 'ticket_exchange',
  },
  website: {
    label: 'Website',
    value: 'website',
  },
  box_office: {
    label: 'Box office',
    value: 'box_office',
  },
  transfer: {
    label: 'Ticket Transfer',
    value: 'ticket_transfer',
  },
}

// A-Za-z: allows only English letters
// ': allows apostrophes
// \\-: allows hyphens
// space: allows spaces
export const namePatternMatch = "^[A-Za-z'\\- ]+$"

export const getSinglarOrPluralWord = (amount, text = 'Ticket') => {
  const pluralChars = text === 'Guest Pass' ? 'es' : 's'
  return isPlural(amount) ? `${text}${pluralChars}` : `${text}`
}

export const isPlural = (amount) => {
  return amount === 0 || amount > 1
}

// generate random color -> used in seat map and offers to generate random colors for price levels and custom offers
export const generateRandomColor = () => {
  // Generate random values for red, green, and blue - between 0 and 255
  const red = Math.floor(Math.random() * 256)
  const green = Math.floor(Math.random() * 256)
  const blue = Math.floor(Math.random() * 256)

  // Create the color string in hexadecimal format - #RRGGBB
  const color = `#${red.toString(16).padStart(2, '0')}${green
    .toString(16)
    .padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`

  return color
}

// sorts two numbers
// - Sorts letters — "A", "B", "C"
// - Sorts numbers inside strings — "Item 2", "Item 10"
// - Handles mixed cases — "a2", "A10"
// used to display tickets in my wallet pages
const compareAlphanumeric = (a, b) => {
  // Use localeCompare with numeric option for natural sorting
  // Treat null/undefined as empty strings for comparison
  const valA = String(a ?? '')
  const valB = String(b ?? '')
  return valA.localeCompare(valB, undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

// Include in each ticket a property: eventStart ( event.start ) for grouping early first by event
export const sortTickets = (tickets) => {
  return [...(tickets || [])].sort((a, b) => {
    // Ensure we have valid objects
    if (!a || !b) return 0

    // PRIMARY: Sort by eventStart (earliest events first)
    const aEventStart = a.eventStart || ''
    const bEventStart = b.eventStart || ''
    if (aEventStart && bEventStart) {
      const eventStartCompare = new Date(aEventStart) - new Date(bEventStart)
      if (eventStartCompare !== 0) {
        return eventStartCompare
      }
    }

    // SECONDARY: Group tickets by eventId (keeps same event together)
    const aEventId = a.eventId || ''
    const bEventId = b.eventId || ''
    const eventIdCompare = aEventId.localeCompare(bEventId)
    if (eventIdCompare !== 0) {
      return eventIdCompare
    }

    // WITHIN EACH EVENT GROUP: Sort by seating details

    // 1. General Admission tickets come first within the event
    const aGA = Boolean(a.generalAdmission)
    const bGA = Boolean(b.generalAdmission)

    if (aGA && !bGA) {
      return -1 // a (GA) comes first
    }
    if (!aGA && bGA) {
      return 1 // b (GA) comes first
    }

    // 2. If both are GA, sort by section only
    if (aGA && bGA) {
      return compareAlphanumeric(a.sectionNumber, b.sectionNumber)
    }

    // 3. For assigned seating: section → row → seat
    const sectionCompare = compareAlphanumeric(a.sectionNumber, b.sectionNumber)
    if (sectionCompare !== 0) {
      return sectionCompare
    }

    const rowCompare = compareAlphanumeric(a.rowNumber, b.rowNumber)
    if (rowCompare !== 0) {
      return rowCompare
    }

    const seatCompare = compareAlphanumeric(a.seatNumber, b.seatNumber)
    if (seatCompare !== 0) {
      return seatCompare
    }

    // Final tiebreaker for identical tickets
    const aId = String(a.id || a.checkInCode || '')
    const bId = String(b.id || b.checkInCode || '')
    return aId.localeCompare(bId)
  })
}

// Helper function to format seat ranges
export const formatSeatRange = (seatNumbers) => {
  if (!seatNumbers || seatNumbers.length === 0) return ''

  // Normalize, dedupe, and sort seat numbers numerically.
  const normalizedSeats = seatNumbers
    .map((seat) => parseInt(seat) || 0)
    .filter((seat) => seat > 0)
  const sortedSeats = [...new Set(normalizedSeats)].sort((a, b) => a - b)

  if (sortedSeats.length === 0) return ''

  const ranges = []
  let start = sortedSeats[0]
  let end = start

  for (let i = 1; i < sortedSeats.length; i++) {
    if (sortedSeats[i] === end + 1) {
      // Consecutive seat
      end = sortedSeats[i]
    } else {
      // Gap found, finalize current range
      if (start === end) {
        ranges.push(start.toString())
      } else {
        ranges.push(`${start}-${end}`)
      }
      start = sortedSeats[i]
      end = start
    }
  }

  // Add the final range
  if (start === end) {
    ranges.push(start.toString())
  } else {
    ranges.push(`${start}-${end}`)
  }

  return ranges.join(', ')
}

export const formatTicketLocationValue = (value, fallback = 'GA') =>
  value == null || String(value).trim() === '' ? fallback : value

// have to pass package_tickets if order is a package
// else tickets/originalTickets
export const formatTicketGroups = (tickets) => {
  if (!tickets) return
  const groups = groupTickets(tickets)
  const modifiedGroups = groups.map((group) => {
    if (group.generalAdmission) {
      const rowNumbers = [
        ...new Set(
          group.tickets
            .map((ticket) => ticket.rowNumber ?? ticket.row_number)
            .filter(
              (rowNumber) =>
                rowNumber != null && String(rowNumber).trim() !== ''
            )
        ),
      ]
      const seatNumbers = [
        ...new Set(
          group.tickets
            .map((ticket) => ticket.seatNumber ?? ticket.seat_number)
            .filter(
              (seatNumber) =>
                seatNumber != null && String(seatNumber).trim() !== ''
            )
        ),
      ]
      const allSeatsAreNumeric = seatNumbers.every((seatNumber) =>
        /^\d+$/.test(String(seatNumber).trim())
      )

      return {
        sectionNumber: group.sectionNumber,
        rowNumber: rowNumbers.join(', ') || null,
        seats:
          (allSeatsAreNumeric ? formatSeatRange(seatNumbers) : '') ||
          seatNumbers.join(', ') ||
          null,
        ticketCount: group.tickets.length,
        tickets: group.tickets,
        generalAdmission: true,
        offer: group.offer, // Include offer information
      }
    } else {
      const seatNumbers = group.tickets
        .map((ticket) => ticket.seatNumber || ticket.seat_number)
        .filter((seat) => seat != null)

      const seatRange = formatSeatRange(seatNumbers)
      return {
        sectionNumber: group.sectionNumber,
        rowNumber: group.rowNumber,
        seats: seatRange || null,
        ticketCount: group.tickets.length,
        tickets: group.tickets,
        generalAdmission: false,
        offer: group.offer, // Include offer information
      }
    }
  })

  return sortTickets(modifiedGroups)
}

// Updated groupTickets function to also group by offer.name
const groupTickets = (tickets) => {
  // Guard against undefined/placeholder entries
  const safeTickets = (tickets || []).filter(Boolean)

  const grouped = safeTickets.reduce((acc, ticket) => {
    // Create a grouping key that includes offer.name
    const offerName = ticket?.offer?.name || 'no-offer'
    const isGenAdmission = ticket?.generalAdmission || ticket?.general_admission
    const sectionNumber = ticket?.sectionNumber || ticket?.section_number
    const rowNumber = ticket?.rowNumber || ticket?.row_number
    const key = isGenAdmission
      ? `${sectionNumber}-${offerName}`
      : `${sectionNumber}-${rowNumber}-${offerName}`
    if (!acc[key]) {
      acc[key] = {
        generalAdmission: isGenAdmission,
        sectionNumber: sectionNumber,
        rowNumber: rowNumber,
        offer: ticket.offer, // Store the offer object
        tickets: [],
      }
    }

    acc[key].tickets.push(ticket)
    return acc
  }, {})

  return Object.values(grouped)
}

// in the format of Sec 1 • Row 1 • Seat 1 or Sec 1 (for GA)
export const formatSectionRowSeat = (ticket) => {
  const parts = [`Sec ${ticket.sectionNumber}`]
  if (ticket?.rowNumber != null && ticket.rowNumber !== '')
    parts.push(`Row ${ticket.rowNumber}`)
  if (
    (ticket?.seats != null && ticket.seats !== '') ||
    (ticket?.seatNumber != null && ticket.seatNumber !== '')
  )
    parts.push(`Seat ${ticket.seats ?? ticket.seatNumber}`)
  const offerInfo = resolvePrintedOfferInfo(ticket)
  if (offerInfo) parts.push(offerInfo.label)
  return parts.join(' • ')
}

export const formatSeatsForOrderListing = (seatTickets, emptyValue = '—') => {
  if (!seatTickets?.length) return emptyValue

  const groups = formatTicketGroups(seatTickets)
  if (!groups?.length) return emptyValue

  return groups
    .map((group) => {
      if (group.generalAdmission) {
        return `Sec ${group.sectionNumber ?? 'N/A'}`
      }

      const parts = [`Sec ${group.sectionNumber ?? 'N/A'}`]
      if (group.rowNumber != null && group.rowNumber !== '') {
        parts.push(`Row ${group.rowNumber}`)
      }
      if (group.seats != null && group.seats !== '') {
        parts.push(`Seat ${group.seats}`)
      }
      return parts.join(' • ')
    })
    .join(', ')
}

// Give index of pl or offer -> get color in order
export const generatePredeterminedColor = (index) => {
  const COLOR_LIST = [
    '#F032E6',
    '#F58231',
    '#FFE119',
    '#E6194B',
    '#DCBEFF',
    '#800000',
    '#FFD8B1',
    '#3CB44B',
    '#BFEF45',
    '#000075',
    '#9A6324',
    '#42D4F4',
    '#AAFFC3',
    '#808000',
    '#911EB4',
    '#469990',
    '#FABED4',
    '#A9A9A9',
  ]

  // Use modulo operator to wrap around when index exceeds array length
  return COLOR_LIST[index % COLOR_LIST.length]
}

export const scrollToTop = () => {
  return window.scrollTo(0, 0)
}

/**
 * Scrolls the open modal's content to the top.
 * With modal-dialog-scrollable, the scroll container is .modal-body.
 */
export const scrollToTopOfModal = () => {
  const modalBody = document.querySelector('.modal.show .modal-body')
  if (modalBody) {
    modalBody.scrollTop = 0
  } else {
    const modalDialog = document.querySelector('.modal.show .modal-dialog')
    if (modalDialog) {
      modalDialog.scrollTop = 0
    } else {
      window.scrollTo(0, 0)
    }
  }
}
// check if user has permission (stable CMS slug on organization-permission)
export const checkPermission = (userPermissions, slug) => {
  return userPermissions?.organization_permissions?.some((userPerm) => {
    const permSlug = userPerm?.slug ?? userPerm?.attributes?.slug
    return permSlug === slug
  })
}

const checkUrl = (url) => {
  var expression = /login|\/signup|register/g
  var regex = new RegExp(expression)
  return regex.test(url)
}

export const fullHeightContainer = (el) => {
  el.classList.add('container--full-height')

  const root = document.getElementById('root')

  root.classList.add('full-height-flex')
}

export const removeFullHeightContainer = (el) => {
  el.classList.remove('container--full-height')

  const root = document.getElementById('root')

  root.classList.remove('full-height-flex')
}

export const changeBackground = (url) => {
  if (checkUrl(url) || /\/fulfillment\/?$/g.test(url)) {
    document.body.classList.add('backgroundWhite')
  } else {
    document.body.classList.remove('backgroundWhite')
  }
}

export const toggleContainer = (url) => {
  const mainContainer = document.querySelector('#main-container')
  if (!mainContainer) return

  // Logic for adding sidebar container - these pages contain sidebar
  // - package pages (not packages - it ends ($), or it continues cleanly with another path segment like / package/123) but not the package seatmap/assign page
  // - event pages but not the seatmap/assign page or events/
  // - flex package pages, matched the same way as package pages so sibling routes
  //   such as /flex-packages and /flex-package-sales-summary keep the default container
  if (
    /(\/package(?:\/|$)(?!.*seatmap\/assign)|\/event\b(?!.*seatmap\/assign)|\/flex-package(?:\/|$)|\/settings)/g.test(
      url
    )
  ) {
    mainContainer.classList.remove('container')
    mainContainer.classList.add('sidebar-container')
  }
  // Logic for removing all containers
  // remove all containers from package/../seatmap/assign page
  // remove all containers from event/../seatmap/assign page
  else if (
    /\/package\/[^/]+\/seatmap\/assign/g.test(url) ||
    /\/event\/[^/]+\/seatmap\/assign/g.test(url)
  ) {
    mainContainer.classList.remove('container', 'sidebar-container')
  }
  // remove all containers from fulfillment kiosk page
  else if (/\/fulfillment\/?$/g.test(url)) {
    mainContainer.classList.remove('container', 'sidebar-container')
  }
  // Default case - add container class to all other pages
  else {
    mainContainer.classList.add('container')
    mainContainer.classList.remove('sidebar-container')
  }
}

// is string (date) in UTC format
export const isISOString = (date) => {
  const iso8601Regex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/
  return iso8601Regex.test(date)
}

// reuseable validation - date cannot be after start
// used for general onsale, event visibility -> event details, availability period -> offers, publish (both for events and packages)
// used for scheduled contact attendees
export const isEventDateTbd = (event) =>
  Boolean(event?.date_tbd || event?.start_tbd)

export const isEventTimeTbd = (event) =>
  isEventDateTbd(event) || Boolean(event?.time_tbd)

export const isEventStartTbd = isEventDateTbd

export const isTimeAfterStart = (date, obj, start) => {
  if (!date || isEventDateTbd(obj)) return false
  const startValue =
    start || getFormattedTimezoneDate(obj?.start, obj?.timezone)
  if (!startValue) return false
  const baseline = moment(startValue)
  if (!baseline.isValid()) return false
  if (isEventTimeTbd(obj)) {
    return moment(date).isAfter(baseline.clone().endOf('day'))
  }
  return moment(date).isAfter(baseline)
}

// reuseable validation - get date cannot be after start error message
// used for general onsale, event visibility -> event details, availability period -> offers, publish (both for events and packages)
// used for scheduled contact attendees
export const getIsTimeAfterStartErrorMsg = (
  obj,
  start,
  isEvent = true,
  isPackage = false
) => {
  if (isEventTimeTbd(obj)) {
    return 'Time cannot be after the end of the event date'
  }
  return `Time cannot be after ${
    isEvent ? 'event' : isPackage ? 'package' : 'flex package'
  } start time (${formatDateTime(
    moment(start || getTimezoneDate(obj?.start, obj?.timezone)),
    'timeOnly'
  )})`
}

// reuseable validation - date cannot be after general onsale
// used for event visibility -> event details, schedule publish date (event visibility) -> publish
export const isTimeAfterGeneralOnsale = (date, event, generalOnsale) => {
  return generalOnsale || event?.generalOnsale
    ? moment(date).isAfter(
        moment(
          generalOnsale ||
            getFormattedTimezoneDate(event?.generalOnsale, event?.timezone)
        )
      )
    : false
}

// reuseable validation - date cannot be after general onsale
// used for event visibility -> event details, schedule publish date (event visibility) -> event status
export const getIsTimeAfterGeneralOnsaleErrorMsg = (event, generalOnsale) => {
  return `Time cannot be after general on-sale time (${formatDateTime(
    moment(
      generalOnsale || getTimezoneDate(event?.generalOnsale, event?.timezone)
    ),
    'timeOnly'
  )})`
}

// reuseable validation - date cannot be before event visibility
// used for general onsale -> event details, edit general onsale availability period -> offers
export const isTimeBeforeEventVisibility = (date, event, visibility) => {
  return visibility || event?.eventVisibility
    ? moment(date).isBefore(
        moment(
          visibility ||
            getFormattedTimezoneDate(event?.eventVisibility, event?.timezone)
        )
      )
    : false
}

// reuseable validation - get date cannot be before event visibility error message
// used for general onsale -> event details, edit general onsale availability period -> offers
export const getIsTimeBeforeEventVisibilityErrorMsg = (event, visibility) => {
  return `Time cannot be before event visibility time (${formatDateTime(
    moment(
      visibility || getTimezoneDate(event?.eventVisibility, event?.timezone)
    ),
    'timeOnly'
  )})`
}

// breakdown modal -> create offers, reports
export const calculateFees = (ticket, feeStructure, taxRates, event) => {
  const toNum = (value) => {
    const parsed =
      typeof value === 'string' ? Number.parseFloat(value) : Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const roundTo2 = (value) => {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0
  }
  const normalizeRate = (value) => {
    const numeric = toNum(value)
    return numeric > 1 ? numeric / 100 : numeric
  }

  const ticketCost = toNum(
    ticket?.offerPrice ?? ticket?.cost ?? ticket?.attributes?.cost
  )
  const ticketFacilityFee = toNum(ticket?.fee ?? ticket?.attributes?.fee)
  // For unassigned custom inventory, seat count can be 0 while pricing is still
  // calculated per ticket. Clamp to at least 1 so fee rules resolve correctly.
  const parsedSeatCount = Number.parseInt(ticket?.seats, 10)
  const seatCount = Number.isFinite(parsedSeatCount)
    ? Math.max(parsedSeatCount, 1)
    : 1

  const organizationFeeConfig =
    event?.organization_fee_config ||
    event?.organization?.organization_fee_config ||
    event?.organizationFeeConfig ||
    event?.organization?.organizationFeeConfig

  const serviceFeePerTicketFromConfig = selectOrgConfigServiceFeePerTicket(
    organizationFeeConfig,
    seatCount,
    ticketCost,
    {
      source: 'website',
      category: 'service',
      productType: 'tickets',
    }
  )

  const shouldIncludeServiceFees = event?.includeServiceFees !== false
  let serviceFees = 0

  if (shouldIncludeServiceFees && ticketCost > 0) {
    if (serviceFeePerTicketFromConfig !== null) {
      serviceFees = serviceFeePerTicketFromConfig
    } else if (ticketCost < 50) {
      serviceFees = toNum(feeStructure?.primaryUnder50) || 2
    } else {
      const over50Rate = toNum(feeStructure?.primaryOver50)
      const rate =
        Number.isFinite(over50Rate) && over50Rate !== 0
          ? over50Rate / 100
          : 0.021
      serviceFees = roundTo2(rate * ticketCost)
    }
  }

  // Venue rates are stored as decimals (0.07 = 7%). Older records sometimes
  // use a percent (7). Dividing every value by 100 turns 0.07 into 0.07%
  // and understates tax. Checkout accepts either form.
  const localTaxRate = normalizeRate(
    taxRates?.combinedTaxRate ?? taxRates?.estimatedCombinedRate
  )

  const salesTax =
    ticketCost > 0 && event?.includeTaxes
      ? roundTo2((ticketCost + ticketFacilityFee + serviceFees) * localTaxRate)
      : 0

  const ticketCostWithFees =
    ticketCost > 0 ? roundTo2(ticketCost + serviceFees + ticketFacilityFee) : 0
  const ticketCostWithFeesAndTax =
    ticketCost > 0 ? roundTo2(ticketCostWithFees + salesTax) : 0

  const stripePercentage = normalizeRate(
    feeStructure?.stripeServicePecentage ??
      feeStructure?.stripeServicePercentage
  )
  const stripeFixedCharge = toNum(feeStructure?.stripeCharge)

  const paymentProcessingFee =
    ticketCost > 0
      ? roundTo2(
          ticketCostWithFeesAndTax * stripePercentage + stripeFixedCharge
        )
      : 0

  const chargeBackProtection = 0
  const totalFees =
    ticketCost > 0
      ? roundTo2(
          serviceFees + ticketFacilityFee + salesTax + paymentProcessingFee
        )
      : 0
  const totalDue =
    ticketCost > 0 ? roundTo2(ticketCost + totalFees + chargeBackProtection) : 0
  const payout = ticketCost > 0 ? roundTo2(ticketCost + ticketFacilityFee) : 0

  return {
    ticketCost,
    serviceFees,
    serviceFee: serviceFees,
    ticketFacilityFee,
    taxPerTicket: salesTax,
    salesTax,
    paymentProcessingFee,
    chargeBackProtection,
    ticketCostWithFees,
    ticketCostWithFeesAndTax,
    totalFees,
    totalDue,
    combinedTaxRate: localTaxRate,
    payout,
  }
}

export const formatNumber = (num) => {
  return parseFloat(num || 0).toLocaleString()
}

// currency changes to what the host is set to
// rounds decimals to 2
// adds comma separator
export const formatCurrency = (num) => {
  return parseFloat(num || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export const removeHyphens = (string, replaceChar = ' ') => {
  return string?.replaceAll('_', replaceChar)
}

// remove hyphens and capitalize first letter of each word
export const formatString = (string, replaceChar = ' ', joinChar = ' ') => {
  let formattedString = removeHyphens(string, replaceChar)
  return (formattedString = capitalizeString(
    formattedString,
    replaceChar,
    joinChar
  ))
}

// capitalize first letter of each word
export const capitalizeString = (string, replaceChar = ' ', joinChar = ' ') => {
  return string
    ? string
        .split(replaceChar)
        .map((word) => capitalizeWord(word))
        .join(joinChar)
    : ''
}

const capitalizeWord = (word) => {
  if (!word) return '' // Handle empty strings
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

// format phone number
export const formatPhoneNumber = (number) => {
  return number
    ? `${number?.toString().substring(0, 2)} (${number
        ?.toString()
        .substring(2, 5)}) ${number?.toString().substring(5, 8)}-${number
        ?.toString()
        .substring(8)}`
    : 'N/A'
}

// sort attractions by their order
export const sortAttractions = (attractions) => {
  return attractions.sort((a, b) => a.order - b.order)
}

// format short date for tickets
// display only date and time
export const formatShortDate = (date, formatter) => {
  if (!date) return
  switch (formatter) {
    case 'dateOnly':
      return date?.format('MMM D, YYYY')

    case 'dateTime':
      return date?.format('MMM D, YYYY h:mm A')

    case 'monthYearOnly':
      return date?.format('MMM YYYY')

    case 'monthDay':
      return date?.format('MMM D')

    default:
      return date?.format('MMM D, h:mm A')
  }
}

// ── Package/event name display helpers (payment listings, daily settlement) ──

// empty display value for all reports and excel spreadsheets
export const EMPTY_DISPLAY_VALUE = '—'

/**
 * Returns true if a season package order should show its event name.
 * Only transfer and ticket-exchange package orders display event names; else package name
 * sources (e.g. website, box office, ticket assignment) do not.
 */
export const shouldShowEventNameForPackageOrder = (order) =>
  Boolean(order?.packageOrder) &&
  (order?.source === ORDER_SOURCE.TRANSFER ||
    order?.source === ORDER_SOURCE.TICKET_EXCHANGE)

/**
 * Returns the package name for ticket-assignment or website package orders, otherwise null
 */
const getPackageNameForPackageOrder = (order) =>
  (order?.packageOrder ||
    order?.productType === PRODUCT_TYPE.SEASON_PACKAGES) &&
  (order?.source === ORDER_SOURCE.TICKET_ASSIGNMENT ||
    order?.source === ORDER_SOURCE.WEBSITE)
    ? order?.packageName?.trim() || null
    : null

/**
 * Returns true if the order is a flex-pack voucher redemption (event-tied),
 * not the original flex pack purchase.
 */
export const isFlexPackVoucherRedemption = (order) => {
  const voucherFlag = order?.voucher === true || order?.voucher === 't'
  const paymentMethod = String(
    order?.paymentMethod || order?.paymentMethodType || ''
  ).toLowerCase()
  const paymentType = String(
    order?.paymentType || order?.paymentOption || ''
  ).toLowerCase()
  return voucherFlag || paymentMethod === 'voucher' || paymentType === 'voucher'
}

/**
 * Returns the display name for the Event column (event name, flex pack name, or package name).
 * - Ticket-assignment or website package orders: package name
 * - Season packages (non-transfer/exchange): "-"
 * - Flex packs and others: eventName or packageName from the order
 * @param {Object} order - Payment listing or order object
 */
export const getEventNameForOrder = (order) => {
  const packageName = getPackageNameForPackageOrder(order)
  if (packageName) return packageName
  if (order?.productType === PRODUCT_TYPE.ACCESS_PASS) {
    return (
      order?.eventName?.trim() ||
      order?.packageName?.trim() ||
      EMPTY_DISPLAY_VALUE
    )
  }
  if (
    order?.productType === PRODUCT_TYPE.SEASON_PACKAGES &&
    !shouldShowEventNameForPackageOrder(order)
  ) {
    return EMPTY_DISPLAY_VALUE
  }
  return order?.eventName ?? order?.packageName ?? EMPTY_DISPLAY_VALUE
}

/**
 * Returns the formatted event date for display, or "-" when not applicable.
 * Season package orders (non-transfer/exchange) and flex pack purchases hide the
 * event date; voucher redemptions keep it because they are tied to a specific event.
 * @param {Object} order - Payment listing or order object
 * @param {string} timezone - IANA time zone identifier used when converting the event
 *   start date for display (e.g. "America/New_York").
 */
export const getEventDateForOrder = (order, timezone) => {
  const isSeasonPackage = order?.productType === PRODUCT_TYPE.SEASON_PACKAGES
  const isFlexPackPurchase =
    order?.productType === PRODUCT_TYPE.FLEX_PACKAGES &&
    !isFlexPackVoucherRedemption(order)

  if (
    (isSeasonPackage || isFlexPackPurchase) &&
    !shouldShowEventNameForPackageOrder(order)
  ) {
    return EMPTY_DISPLAY_VALUE
  }
  return order?.eventStart
    ? (formatDateTime(getTimezoneDate(order.eventStart, timezone)) ??
        EMPTY_DISPLAY_VALUE)
    : EMPTY_DISPLAY_VALUE
}

// format full date
export const formatDateTime = (date, formatter) => {
  if (!date) return
  const normalizedDate =
    typeof date?.format === 'function' ? date : moment(date)
  if (!normalizedDate?.isValid?.()) return
  switch (formatter) {
    case 'dateOnly':
      return normalizedDate.format('ddd, MMM D, YYYY')

    case 'dateOnlyMdY':
      return normalizedDate.format('MM/DD/YYYY')

    case 'timeOnly':
      return normalizedDate.format('h:mm A')

    default:
      return normalizedDate.format('ddd, MMM D, YYYY h:mm A')
  }
}

// get current time in specific timezone
export const getNowInTimezone = (timezone) => {
  if (!timezone) return moment()
  const tz = SharedDateTime.ensureIanaTimeZone(timezone, 'UTC')
  // Return a moment object representing 'now' in the target timezone
  const now = new Date()
  const zoned = SharedDateTime.toZonedTime(now.toISOString(), tz)
  return moment(zoned)
}

// converts date to timezone using IANA tz (no fixed offsets)
// returns moment object - used with other format dates helper methods
export const getTimezoneDate = (date, timezone, keepLocalTime = false) => {
  if (!date) return
  // if timezone is not provided, return the date as is
  if (!timezone) return moment(date)
  const tz = SharedDateTime.ensureIanaTimeZone(timezone, 'UTC')
  // If keepLocalTime is true, treat input as local wall time in tz and convert accordingly
  if (keepLocalTime) {
    // When keeping local time, we interpret 'date' as already being wall time in tz
    // Ensure we output a Date that reflects that wall time
    const localIso =
      typeof date === 'string'
        ? date
        : moment(date).format('YYYY-MM-DDTHH:mm:ss')
    const utc = SharedDateTime.toUtcFromZoned(localIso, tz)
    const zoned = SharedDateTime.toZonedTime(utc, tz)
    return moment(zoned)
  }
  // Otherwise, shift the instant into the target zone for display
  const inputIso =
    typeof date === 'string'
      ? date
      : date instanceof Date
        ? date.toISOString()
        : moment(date).toISOString()
  const zoned = SharedDateTime.toZonedTime(inputIso, tz)
  return moment(zoned)
}

export const formatEventSchedule = (event, timezone, options = {}) => {
  if (isEventDateTbd(event)) return 'Date & Time TBD'
  const zoned = getTimezoneDate(event?.start, timezone)
  if (!zoned?.format) return ''
  const dateLabel = zoned.format(options.includeYear ? 'MMM D, YYYY' : 'MMM D')
  if (isEventTimeTbd(event)) return `${dateLabel} · Time TBD`
  if (event?.display_start_time === false) return dateLabel
  return `${dateLabel} · ${zoned.format('h:mm A')}`
}

export const formatEventStart = (event, timezone, formatter) => {
  if (isEventDateTbd(event)) return 'Date & Time TBD'
  const zoned = getTimezoneDate(event?.start, timezone)
  if (isEventTimeTbd(event)) {
    if (formatter === 'timeOnly') return 'Time TBD'
    if (formatter === 'dateOnly' || formatter === 'dateOnlyMdY') {
      return formatDateTime(zoned, formatter)
    }
    return formatEventSchedule(event, timezone)
  }
  return formatDateTime(zoned, formatter)
}

export const toPickerDate = (value, timezone) => {
  const formatted = getFormattedTimezoneDate(value, timezone)
  if (!formatted) return undefined
  const date = new Date(formatted)
  return Number.isNaN(date.getTime()) ? undefined : date
}

// Date TBD has no public deadline. Time TBD can run through the confirmed day.
export const scheduleCapDate = (event, startDate) => {
  if (isEventDateTbd(event)) return undefined
  const startValue =
    startDate ||
    (event?.start ? toPickerDate(event.start, event?.timezone) : undefined)
  if (!startValue) return undefined
  if (isEventTimeTbd(event)) return moment(startValue).endOf('day').toDate()
  return startValue instanceof Date ? startValue : new Date(startValue)
}

// calls getTimezoneDate to convert date to timezone
// returns string - used for date inputs and to check if two dates are equal
export const getFormattedTimezoneDate = (date, timezone) => {
  if (!date) return
  // if timezone is not provided, return the date as is formatted as "YYYY-MM-DD HH:mm:ss"
  if (!timezone) return moment(date).format('YYYY-MM-DD HH:mm:ss')
  const tz = SharedDateTime.ensureIanaTimeZone(timezone, 'UTC')
  const inputIso =
    typeof date === 'string'
      ? date
      : date instanceof Date
        ? date.toISOString()
        : moment(date).toISOString()
  // Format as "YYYY-MM-DD HH:mm:ss" in the target timezone
  return SharedDateTime.formatInTimeZone(inputIso, tz, 'yyyy-MM-dd HH:mm:ss')
}

// Convert a local date/time in a given timezone to UTC ISO string (Z)
// date can be a Date, moment, or ISO-like string representing the local wall time in `timezone`
export const toUtcIsoFromTimezone = (date, timezone) => {
  if (!date || !timezone) return
  const tz = SharedDateTime.ensureIanaTimeZone(timezone, 'UTC')
  const localIso =
    typeof date === 'string' ? date : moment(date).format('YYYY-MM-DDTHH:mm:ss')
  // Returns an ISO string in UTC (with Z)
  return SharedDateTime.toUtcFromZoned(localIso, tz)
}

// Availability periods added in the browser have no database row yet. Strapi
// updates repeatable components by their global component id, so sending a
// guessed numeric id would overwrite whichever offer already owns that id.
// These local ids are stripped from the payload so the server creates the row.
const LOCAL_PERIOD_ID_PREFIX = 'local-period-'

let localPeriodIdCounter = 0

export const createLocalPeriodId = () => {
  localPeriodIdCounter += 1
  return `${LOCAL_PERIOD_ID_PREFIX}${localPeriodIdCounter}`
}

export const isLocalPeriodId = (id) =>
  typeof id === 'string' && id.startsWith(LOCAL_PERIOD_ID_PREFIX)

// format full address
export const formatAddress = (obj) => {
  return `${obj?.address[0]?.address_1}, ${capitalizeString(
    obj?.address[0]?.city
  )}, ${obj?.address[0]?.state.toUpperCase()}, ${
    obj?.address[0]?.zipcode
  }, ${obj?.address[0]?.country.toUpperCase()}`
}

// format address - city, state
export const formatShortAddress = (obj) => {
  if (!obj) return
  return `${capitalizeString(
    obj?.address ? obj?.address[0]?.city : obj.city
  )}, ${
    obj?.address
      ? obj?.address[0]?.state?.toUpperCase()
      : obj.state.toUpperCase()
  }`
}

export const formatPermissions = (permissions) => {
  return permissions.reduce(function (r, a) {
    r[a.attributes.key] = r[a.attributes.key] || []
    r[a.attributes.key].push({
      slug: a.attributes.slug,
      name: a.attributes.name,
    })
    return r
  }, Object.create(null))
}

// creating and getting existing member have different object properties
// creating member has name and role property
export const formatMembers = (members) => {
  let arr = []
  members.map((member) =>
    arr.push({
      firstName: member?.firstName || member?.name.split(' ')[0],
      lastName: member?.lastName || member?.name.split(' ')[1],
      role: member?.organization_role || member?.role,
      email: member?.email,
      uuid: member?.uuid,
      pending: member?.pending,
    })
  )
  return arr
}

// used in order list and custom reports
export const getTransactionTypes = () => {
  return [
    {
      label: 'Primary',
      value: 'primary',
    },
    {
      label: 'Resales',
      value: 'resale',
    },
    {
      label: 'Transfers',
      value: 'transfer',
    },
    {
      label: 'Refunds',
      value: 'refund',
    },
  ]
}

export const getAttendeeTransactionType = (attendanceType, ticket) => {
  if (attendanceType === 'ticket') {
    if (ticket?.resale) return 'Resale'

    if (ticket?.transferred) return 'Transferred'
    else return 'Primary'
  }
  if (attendanceType === 'guestPass') return 'Guest Pass'
}

export const getTicketTransactionType = (ticket) => {
  if (ticket?.resale) return 'Resale'

  if (ticket?.transferred) return 'Transferred'
  else return 'Primary'
}

// get ticket object
// used in orders ticket row, reports table
export const getTicket = (order, field) => {
  let obj

  // transfer tickets
  // if (order.status === 'completeFromTransfer') obj = order?.details?.details?.ticket

  // // resale tickets
  // else if (order?.details?.listing) obj = order?.details?.listing?.tickets[0]

  // primary tickets
  obj = order?.tickets

  if (obj) {
    if (field) {
      return obj?.map((o) => o[field])
    } else {
      return obj
    }
  }
}

// get offer associated with bought ticket
export const getOfferName = (ticket) => {
  return ticket?.offer?.name || 'N/A'
}

// get feeDetails object
// used in custom reports order table
export const getFees = (order, field) => {
  let obj

  // transfer tickets
  if (order.status === 'completeFromTransfer')
    obj = order?.details?.details?.feeDetails
  // primary and resale tickets
  else obj = order?.details?.feeDetails

  if (obj) {
    if (field) {
      return obj[field]
    } else {
      return obj
    }
  }
}

// used in resale event report
export const getRoyalties = (order) => {
  const share = Number(order?.resaleOrganizerShare)
  return Number.isFinite(share) ? share : 0
}

export const resaleOrderWasPurchased = (order) => {
  const status = String(order?.status || '').toLowerCase()
  const paymentStatus = String(order?.paymentStatus || '').toLowerCase()
  if (status === 'refunded' || paymentStatus === 'refunded') return false
  return status === 'complete' || paymentStatus === 'paid'
}

export const isMatching = (input1, input2) => {
  // compare strings only if they are not blank
  if (input1 !== '' && input2 !== '') {
    return input1 === input2
  }
}

// event is past
export const isEventPast = (event) => {
  return event?.status === 'complete'
}

export const emailPatternMatch = (val) => {
  const trimmed = String(val ?? '').trim()
  if (!trimmed) return true
  // Allow common admin formats (e.g. plus-addressing); avoid regex /g + .test() pitfalls
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}

export const getUrl = (link, params) => {
  const url = link
  return params ? `${url}${params}` : url
}

export const copy = (text, setter, params) => {
  const url = getUrl(text, params)
  /* Copy the text */
  navigator.clipboard.writeText(url)
  if (setter) setter(true)
}

export const setHeaders = (
  nameOfReport,
  organizationName,
  worksheet,
  customHeaders
) => {
  worksheet.addRow([`${nameOfReport} Report`])
  worksheet.addRow([organizationName])
  customHeaders?.map((header) => {
    worksheet.addRow([header])
  })
  worksheet.addRow([`Generated: ${new Date().toLocaleString()}`])
  worksheet.addRow([])
}

export const styleHeaders = (worksheet) => {
  // Style title rows
  worksheet.getCell('A1').style = {
    font: { bold: true, size: 16 },
    alignment: { horizontal: 'center' },
  }
  worksheet.getCell('A2').style = {
    font: { bold: true, size: 12 },
    alignment: { horizontal: 'center' },
  }

  const lastColumn = worksheet.getColumn(worksheet.lastColumn.number).letter

  //   Merge title cells with correct column references
  worksheet.mergeCells(`A1:${lastColumn}1`)
  //   Merge title cells with correct column references
  worksheet.mergeCells(`A2:${lastColumn}2`)
}

let workbookName

export const exportData = (
  data,
  exportedData,
  eventName,
  packageName,
  flexPackName,
  organizationName,
  capacity,
  contextType
) => {
  workbookName = exportedData
  const workbook = createWorkbook(workbookName)
  const worksheet = addWorksheet(workbookName, workbook)

  const reportLabel =
    exportedData === 'season_holders'
      ? 'Package Holders'
      : exportedData === 'access_pass_holders'
        ? 'Access Pass Holders'
        : capitalizeString(exportedData, '_', ' ')

  const contextHeader = eventName
    ? `Event: ${eventName}`
    : packageName
      ? exportedData === 'season_holders' && contextType !== 'package'
        ? `Season: ${packageName}`
        : `Package: ${packageName}`
      : flexPackName
        ? `Flex Package: ${flexPackName}`
        : null

  setHeaders(reportLabel, organizationName, worksheet, [
    ...(contextHeader ? [contextHeader] : []),
  ])

  const headerNumRows = DEFAULTNUMHEADERS + 1

  setColumnsAndRows(
    Object.keys(format(...data)),
    Object.values(data),
    format,
    worksheet
  )

  if (workbookName === 'sales') {
    currencyFormat(2, worksheet, headerNumRows)

    numberFormat(3, worksheet, headerNumRows)

    currencyFormat(4, worksheet, headerNumRows)

    currencyFormat(5, worksheet, headerNumRows)
    // if two tickets, rowCount = 3
    worksheet.getCell(`A${worksheet.rowCount + 1}`).value = 'Total'
    // if two tickets, rowCount = 4
    worksheet.getRow(`${worksheet.rowCount}`).font = { bold: true }

    if (worksheet.getColumn(5).key === 'net_sales') {
      worksheet.getCell(`A${worksheet.rowCount + 1}`).value =
        'Tickets remaining'
      // if two tickets, rowCount = 5
      worksheet.getRow(`${worksheet.rowCount}`).font = { bold: true }
      worksheet.getCell(`A${worksheet.rowCount + 1}`).value = 'Capacity'
      // if two tickets, rowCount = 6
      worksheet.getRow(`${worksheet.rowCount}`).font = { bold: true }
    }
    // for the total row - resale tickets report has less rows than primary tickets report
    let rowCount
    if (worksheet.getColumn(5).key === 'royalties') {
      rowCount = worksheet.rowCount
    } else {
      rowCount = worksheet.rowCount - 2
    }

    // total row
    sumCells('C', rowCount - 1, rowCount, worksheet)
    sumCells('D', rowCount - 1, rowCount, worksheet)
    sumCells('E', rowCount - 1, rowCount, worksheet)

    if (worksheet.getColumn(5).key === 'net_sales') {
      // capacity row
      worksheet.getCell(`C${worksheet.rowCount}`).value = capacity

      // tickets remaining row
      worksheet.getCell(`C${worksheet.rowCount - 1}`).value = {
        formula: `SUM(C${worksheet.rowCount} - C${worksheet.rowCount - 2})`,
      }
    }
  }

  // format rows
  if (workbookName === 'orders') {
    // custom date column - purchased date
    dateFormat(5, worksheet, headerNumRows)
    // custom currency column - total
    currencyFormat(10, worksheet, headerNumRows)
  }

  if (workbookName === 'sales') {
    currencyFormat(2, worksheet, headerNumRows)

    numberFormat(3, worksheet, headerNumRows)

    currencyFormat(4, worksheet, headerNumRows)

    currencyFormat(5, worksheet, headerNumRows)
    // if two tickets, rowCount = 3
    worksheet.getCell(`A${worksheet.rowCount + 1}`).value = 'Total'
    // if two tickets, rowCount = 4
    worksheet.getRow(`${worksheet.rowCount}`).font = { bold: true }

    if (worksheet.getColumn(5).key === 'net_sales') {
      worksheet.getCell(`A${worksheet.rowCount + 1}`).value =
        'Tickets remaining'
      // if two tickets, rowCount = 5
      worksheet.getRow(`${worksheet.rowCount}`).font = { bold: true }
      worksheet.getCell(`A${worksheet.rowCount + 1}`).value = 'Capacity'
      // if two tickets, rowCount = 6
      worksheet.getRow(`${worksheet.rowCount}`).font = { bold: true }
    }
    // for the total row - resale tickets report has less rows than primary tickets report
    let rowCount
    if (worksheet.getColumn(5).key === 'royalties') {
      rowCount = worksheet.rowCount
    } else {
      rowCount = worksheet.rowCount - 2
    }

    // total row
    sumCells('C', rowCount - 1, rowCount, worksheet)
    sumCells('D', rowCount - 1, rowCount, worksheet)
    sumCells('E', rowCount - 1, rowCount, worksheet)

    if (worksheet.getColumn(5).key === 'net_sales') {
      // capacity row
      worksheet.getCell(`C${worksheet.rowCount}`).value = capacity

      // tickets remaining row
      worksheet.getCell(`C${worksheet.rowCount - 1}`).value = {
        formula: `SUM(C${worksheet.rowCount} - C${worksheet.rowCount - 2})`,
      }
    }
  }

  if (workbookName === 'invoices') {
    // currency column - total amount/amount due - assign tickets/assign package
    currencyFormat(5, worksheet, headerNumRows)
    // currency column - amount due - assign tickets
    currencyFormat(6, worksheet, headerNumRows)
  }

  if (workbookName === 'season_holders' && Array.isArray(data) && data.length) {
    const sumSeatCount = data.reduce((sum, holder) => {
      const count = Number(holder?.seat_count ?? holder?.number_of_seats ?? 0)
      return sum + (Number.isFinite(count) ? count : 0)
    }, 0)
    const sumTicketCount = data.reduce((sum, holder) => {
      const count = Number(
        holder?.ticket_count ?? holder?.number_of_tickets ?? 0
      )
      return sum + (Number.isFinite(count) ? count : 0)
    }, 0)
    const sumTotal = data.reduce((sum, holder) => {
      const total = Number(holder?.total ?? 0)
      return sum + (Number.isFinite(total) ? total : 0)
    }, 0)

    const totalRowNumber = worksheet.rowCount + 1
    const totalRow = worksheet.getRow(totalRowNumber)
    const columns = worksheet.columns || []
    const seatCountColIndex = columns.findIndex(
      (col) => col?.key === 'number_of_seats'
    )
    const ticketCountColIndex = columns.findIndex(
      (col) => col?.key === 'number_of_tickets'
    )
    const totalColIndex = columns.findIndex((col) => col?.key === 'total')

    totalRow.getCell(1).value = 'Total'
    if (seatCountColIndex >= 0) {
      totalRow.getCell(seatCountColIndex + 1).value = sumSeatCount
    }
    if (ticketCountColIndex >= 0) {
      totalRow.getCell(ticketCountColIndex + 1).value = sumTicketCount
    }
    if (totalColIndex >= 0) {
      totalRow.getCell(totalColIndex + 1).value = formatCurrency(sumTotal)
    }
    totalRow.font = { bold: true }
    totalRow.commit()
  }

  // save excel worksheet
  saveWorkbook(workbook, workbookName)
    .then()
    .catch((err) => alert(err.message))
}

// tell excel to format values into numbers for column
// numFmt defaults to integer-style; pass e.g. '#,##0.##########' for fractional metrics.
export const numberFormat = (
  colNum,
  worksheet,
  headerRows,
  numFmt = '#,##0'
) => {
  worksheet
    .getColumn(colNum)
    .eachCell({ includeEmpty: false }, (cell, rowNumber) => {
      if (rowNumber <= headerRows) return // skip header(s)
      cell.numFmt = numFmt
    })
}

// tell excel to format values into percent for column
export const percentFormat = (colNum, worksheet, headerRows) => {
  worksheet
    .getColumn(colNum)
    .eachCell({ includeEmpty: false }, (cell, rowNumber) => {
      if (rowNumber <= headerRows) return // skip header(s)
      cell.numFmt = '0.00%'
    })
}

// tell excel to format values into currency for column
export const currencyFormat = (colNum, worksheet, headerRows) => {
  worksheet
    .getColumn(colNum)
    .eachCell({ includeEmpty: false }, (cell, rowNumber) => {
      if (rowNumber <= headerRows) return // skip header(s)
      cell.numFmt = '"$"#,##0.00;[Red]-"$"#,##0.00'
    })
}

// tell excel to format values into date for column
export const dateFormat = (colNum, worksheet, headerRows) => {
  worksheet
    .getColumn(colNum)
    .eachCell({ includeEmpty: false }, (cell, rowNumber) => {
      if (rowNumber <= headerRows) return // skip header(s)
      cell.numFmt = '"ddd, mmm dd, yyyy h:mm AM/PM"'
    })
}

// sum all cells from column
export const sumCells = (
  column,
  totalRowNum,
  rowCount,
  worksheet,
  headerRows,
  startRowNum
) => {
  if (!startRowNum) startRowNum = headerRows + 1
  worksheet.getCell(`${column}${totalRowNum}`).value = {
    formula: `SUM(${column}${startRowNum}:${column}${rowCount})`,
  }
}

export const setColumnsAndRows = (columns, rows, formatter, worksheet) => {
  const cols = addColumns(columns)
  // Define worksheet columns
  worksheet.columns = cols.map((col) => ({ key: col.key }))

  // Determine header row
  const num = worksheet.lastRow ? worksheet.lastRow.number + 1 : 1
  const headerRow = worksheet.getRow(num)

  // Set header values (1-based index)
  headerRow.values = cols.map((col, idx) => col.header)

  // Add data rows
  worksheet.addRows(addRows(rows, formatter))

  styleColumns(headerRow)
  styleRows(worksheet, num)
  styleHeaders(worksheet)
}

const styleRows = (worksheet, headerRows) => {
  // Apply cell style and set all cells to text format in one pass
  worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (rowNumber <= headerRows) return // Skip title/header rows

    row.eachCell({ includeEmpty: true }, (cell) => {
      // Apply custom style while preserving existing
      cell.style = { ...cell.style, ...excelCellStyle }

      // Force all cells to text format
      cell.numFmt = '@'
    })
  })

  // Auto-fit columns based on content
  worksheet.columns.forEach((column) => {
    let maxLength = 0

    column.eachCell({ includeEmpty: true }, (cell) => {
      const cellValue = cell.value ? cell.value.toString() : ''
      maxLength = Math.max(maxLength, cellValue.length)
    })

    // Set column width with buffer and minimum width
    column.width = Math.max(maxLength + 5, 12)
  })
}

const styleColumns = (headerRow) => {
  // Apply header styles
  headerRow.eachCell((cell) => {
    cell.style = {
      ...cell.style, // preserve existing formatting (numFmt, alignment, etc.)
      ...excelHeaderStyle, // apply header-specific styles (font, fill, borders)
    }
  })
}

export const addEmptyRows = (rowsToInsert, worksheet) => {
  // Insert blank rows *after* processing, in reverse to avoid index shifting
  rowsToInsert.reverse().forEach((insertAfter) => {
    worksheet.spliceRows(insertAfter + 1, 0, [])
  })
}

export const styleEmptyRows = (
  emptyRow,
  lastColNumber,
  hasHiddenCol = false
) => {
  // Loop through all columns and create the cells
  for (let col = 1; col <= lastColNumber; col++) {
    const cell = emptyRow.getCell(col) // creates the cell if it doesn't exist
    cell.style = {
      ...cell.style,
      ...excelEmptyCellStyle,
    }
    const lastColumnNumber = hasHiddenCol ? lastColNumber - 1 : lastColNumber
    if (col === lastColumnNumber) {
      const cell = emptyRow.getCell(col) // creates the cell if it doesn't exist
      cell.border = {
        right: { style: 'thin' },
      }
    }
  }
}

export const styleTotalRow = (row, worksheet, hasHiddenCol = false) => {
  if (hasHiddenCol) {
    // Loop through all columns
    worksheet.columns.forEach((col, index) => {
      if (!col.hidden) {
        // Skip hidden columns
        const cell = row.getCell(index + 1) // getCell is 1-based
        cell.style = {
          ...cell.style,
          ...excelTotalCellStyle,
        }
      }
    })
  } else {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.style = {
        ...cell.style, // preserve existing formatting
        ...excelTotalCellStyle, // apply your desired style properties
      }
    })
  }
}

export const styleSubTotalRow = (row, worksheet, hasHiddenCol = false) => {
  if (hasHiddenCol) {
    // Loop through all columns
    worksheet.columns.forEach((col, index) => {
      if (!col.hidden) {
        // Skip hidden columns
        const cell = row.getCell(index + 1) // getCell is 1-based
        cell.style = {
          ...cell.style,
          ...excelTotalSubGroupCellStyle,
        }
      }
    })
  } else {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.style = {
        ...cell.style, // preserve existing formatting
        ...excelTotalCellStyle, // apply your desired style properties
      }
    })
  }
}

const addColumns = (names) => {
  return names.map((name) => {
    return {
      header: `${formatString(`${name}`)}`,
      key: `${name}`,
    }
  })
}

const addRows = (data, formatter) => {
  return data.map((val) => {
    return formatter(val)
  })
}

const format = (data) => {
  switch (workbookName) {
    case 'orders':
      return formatOrder(data)

    case 'attendees':
      return formatAttendee(data)

    case 'sales':
      return formatSale(data)

    case 'invoices':
      return formatInvoice(data)

    case 'package_holders':
      return formatPackageHolder(data)

    case 'season_holders':
      return formatSeasonHolder(data)

    case 'access_pass_holders':
      return formatAccessPassHolder(data)

    default:
      return
  }
}

// event orders, package holders, flex pack holders
const formatOrder = (order) => {
  const {
    orderId,
    source,
    boxOffice,
    email,
    firstName,
    lastName,
    users_permissions_user,
    processedAt,
    total,
    intentDetails,
    event,
    paymentOption,
    paymentStatus,
    paymentMethodType,
    status,
    details,
  } = order

  const tickets = order?.package ? order?.package_tickets : order?.tickets

  const timezone = timezones(
    event?.venue?.timezone ||
      order?.package?.venue?.timezone ||
      order?.flex_pack?.venue?.timezone
  )
  const sourceStatus =
    sourceStatuses[source || (boxOffice && 'box_office') || 'website']

  return {
    order: orderId,
    source: capitalizeString(sourceStatus?.label),
    purchased_by:
      users_permissions_user || firstName || lastName
        ? `${capitalizeString(
            `${users_permissions_user?.firstName || firstName} ${users_permissions_user?.lastName || lastName}`
          )}`
        : 'NO USER',
    email: email || details?.email,
    purchase_date: formatDateTime(getTimezoneDate(processedAt, timezone)),
    offer:
      tickets?.length > 0
        ? formatTicketGroups(tickets)
            ?.map((group) => getOfferName(group))
            ?.join('\n')
        : 'N/A',
    section:
      tickets?.length > 0
        ? formatTicketGroups(tickets)
            ?.map((group) => group?.sectionNumber)
            ?.join('\n')
        : 'N/A',
    row:
      tickets?.length > 0
        ? formatTicketGroups(tickets)
            ?.map((group) =>
              formatTicketLocationValue(
                group?.rowNumber,
                group?.generalAdmission ? 'GA' : 'N/A'
              )
            )
            ?.join('\n')
        : 'N/A',
    seat:
      tickets?.length > 0
        ? formatTicketGroups(tickets)
            ?.map((group) =>
              formatTicketLocationValue(
                group?.seats,
                group?.generalAdmission ? 'GA' : 'N/A'
              )
            )
            ?.join('\n')
        : 'N/A',
    total: total,
    paid:
      source === 'ticket_assignment'
        ? paymentStatus !== 'open'
          ? 'Yes'
          : 'No'
        : 'Yes',
    paid_by:
      !boxOffice && source !== 'transfer'
        ? intentDetails
          ? `${intentDetails?.charges?.data[0]?.payment_method_details?.card?.brand} ${intentDetails?.charges?.data[0]?.payment_method_details?.card?.last4}`
          : `${capitalizeString(paymentMethodType === 'online' ? 'online' : paymentOption)}`
        : 'N/A',
    status: `${paymentStatuses[paymentStatus]?.label || 'Paid'}`,
    tickets_sent:
      source === 'ticket_assignment'
        ? status === 'completeFromAssignment'
          ? 'Yes'
          : 'No'
        : 'N/A',
  }
}

const formatAttendee = (attendee) => {
  const {
    users_permissions_user,
    attendanceType,
    ticket,
    guest_pass,
    checkedIn,
    checkedInTime,
    timezone,
  } = attendee

  let ticketBuyer
  let email
  if (users_permissions_user) {
    ticketBuyer = `${capitalizeString(
      `${users_permissions_user?.firstName} ${users_permissions_user?.lastName}`
    )}`
    email = users_permissions_user.email
  } else {
    ticketBuyer = attendee?.emailAssigned
    email = attendee?.emailAssigned
  }

  return {
    ticket_buyer: ticketBuyer,
    email: email,
    phone_number: `${
      users_permissions_user?.phoneNumber
        ? `${formatPhoneNumber(users_permissions_user?.phoneNumber)}`
        : `${formatPhoneNumber(guest_pass?.phoneNumber)}`
    }`,
    offer: getOfferName(attendee?.ticket || attendee?.guest_pass) || 'N/A',
    status: checkedIn
      ? `Checked in ${formatDateTime(getTimezoneDate(checkedInTime, timezone))}`
      : 'Check in',
    seat: ticket?.generalAdmission
      ? `GA: Sec ${ticket?.sectionNumber}`
      : `Sec ${ticket?.sectionNumber} - Row ${ticket?.rowNumber} - Seat ${ticket?.seatNumber}`,
  }
}

const formatSale = (sale) => {
  const { ticketType, gross, net, price, quantity, details } = sale
  return {
    ticket_type:
      ticketType ||
      details?.listing?.packageName ||
      details?.listing?.tickets?.[0]?.name ||
      details?.listing?.offer?.name,
    price: !details?.listing
      ? parseFloat(price)
      : details?.listing?.askingPrice,
    quantity: !details?.listing
      ? parseFloat(quantity)
      : details?.listing?.quantity,
    gross: !details?.listing
      ? parseFloat(gross)
      : parseFloat(
          details?.listing?.pricing?.ticketCostWithFees ??
            details?.listing?.buyerTotal
        ),
    ...(!details?.listing && { net_sales: parseFloat(net ? net : 0) }),
    ...(details?.listing && {
      order_id: sale.orderId || '',
      royalties: resaleOrderWasPurchased(sale)
        ? parseFloat(getRoyalties(sale))
        : 0,
      status: orderStatuses[sale.status]?.label || sale.status || '',
      purchased: resaleOrderWasPurchased(sale) ? 'Yes' : 'No',
    }),
  }
}

// assign package and assign tickets
const formatInvoice = (invoice) => {
  const {
    purchasedByOrganizationName,
    email,
    firstName,
    lastName,
    phoneNumber,
    total,
    amountDue,
    paymentStatus,
    status,
  } = invoice

  const fullName = `${firstName} ${lastName}`

  return {
    organization: purchasedByOrganizationName || 'N/A',
    ticket_buyer: `${capitalizeString(fullName)}`,
    email: email,
    phone_number: `${formatPhoneNumber(phoneNumber)}` || 'N/A',
    total_amount: total || 0,
    amount_due: amountDue || 0,
    status: `${paymentStatuses[paymentStatus]?.label}`,
    tickets_sent: status === 'completeFromAssignment' ? 'Yes' : 'No',
  }
}

const formatPackageHolder = (holder) => {
  const { firstName, lastName, email, phoneNumber, package_tickets, tickets } =
    holder
  const holderTickets = package_tickets?.length
    ? package_tickets
    : tickets || []
  const groupedTickets = formatTicketGroups(holderTickets) || []
  const fullName = `${firstName || ''} ${lastName || ''}`.trim()

  return {
    holder_name: capitalizeString(fullName || 'N/A'),
    email: email || 'N/A',
    phone: formatPhoneNumber(phoneNumber) || 'N/A',
    section:
      groupedTickets?.length > 0
        ? groupedTickets
            .map((group) => group?.sectionNumber || 'N/A')
            .join('\n')
        : 'N/A',
    row:
      groupedTickets?.length > 0
        ? groupedTickets
            .map((group) =>
              formatTicketLocationValue(
                group?.rowNumber,
                group?.generalAdmission ? 'GA' : 'N/A'
              )
            )
            .join('\n')
        : 'N/A',
    seats:
      groupedTickets?.length > 0
        ? groupedTickets
            .map((group) =>
              formatTicketLocationValue(
                group?.seats,
                group?.generalAdmission ? 'GA' : 'N/A'
              )
            )
            .join('\n')
        : 'N/A',
  }
}

const formatSeasonHolder = (holder) => {
  const fullName =
    `${holder?.first_name || ''} ${holder?.last_name || ''}`.trim()
  const isComp =
    holder?.complimentary === true ||
    holder?.complimentary === 't' ||
    holder?.complimentary === 1 ||
    holder?.status === 'comp'
  const status = isComp
    ? orderStatuses.comp || paymentStatuses.comp
    : paymentStatuses[holder?.status] || orderStatuses[holder?.status]
  const purchaseDate =
    holder?.processed_at || holder?.processedAt || holder?.purchase_date
  const datePaid = holder?.date_paid || holder?.datePaid

  return {
    seats: holder?.seats || '',
    number_of_seats: holder?.seat_count ?? holder?.number_of_seats ?? 0,
    number_of_tickets: holder?.ticket_count ?? holder?.number_of_tickets ?? 0,
    package: holder?.package_name || '',
    name: capitalizeString(fullName || 'N/A'),
    email: holder?.email || 'N/A',
    phone_number: formatPhoneNumber(holder?.phone_number) || 'N/A',
    address: holder?.address || '',
    purchase_date: purchaseDate
      ? formatDateTime(purchaseDate, 'dateOnlyMdY') || ''
      : '',
    date_paid: datePaid ? formatDateTime(datePaid, 'dateOnlyMdY') || '' : '',
    amount_due: formatCurrency(holder?.amount ?? 0),
    total: formatCurrency(holder?.total ?? 0),
    status: status?.label || holder?.status || '',
  }
}

const formatAccessPassHolder = (holder) => {
  const fullName =
    `${holder?.first_name || ''} ${holder?.last_name || ''}`.trim() ||
    holder?.name ||
    ''

  return {
    pass_name: holder?.pass_name || '',
    check_in_code: holder?.check_in_code || '',
    name: capitalizeString(fullName || 'N/A'),
    email: holder?.email || 'N/A',
    phone_number: formatPhoneNumber(holder?.phone_number) || 'N/A',
    address: holder?.address || '',
    events: holder?.events || '',
    seat: holder?.seat || '',
    status: capitalizeString(holder?.status || ''),
    order_id: holder?.order_id || '',
    issued: holder?.issued || '',
  }
}

export const createWorkbook = () => {
  const workbook = new ExcelJS.Workbook()

  return workbook
}

export const addWorksheet = (name, workbook) => {
  const sheet = workbook.addWorksheet(name, {
    properties: { defaultColWidth: 10 },
    pageSetup: { orientation: 'landscape', showGridLines: true },
  })

  return sheet
}

export const saveWorkbook = async (workbook, title) => {
  workbook.xlsx.writeBuffer().then(function (buffer) {
    saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      }),
      `${title} - ${new Date()}.xlsx`
    )
  })
}

export const convertBlobToDataURL = async (blobUrl) => {
  try {
    // Fetch the blob data from the blob URL
    const response = await fetch(blobUrl)
    const blob = await response.blob()

    // Convert the blob to a Data URL
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Error converting blob to Data URL:', error)
  }
}

// used to upload images in create event settings and packages
export const b64toBlob = (b64Data, contentType, sliceSize) => {
  contentType = contentType || ''
  sliceSize = sliceSize || 512

  var byteCharacters = atob(b64Data)
  var byteArrays = []

  for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    var slice = byteCharacters.slice(offset, offset + sliceSize)

    var byteNumbers = new Array(slice.length)
    for (var i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    var byteArray = new Uint8Array(byteNumbers)

    byteArrays.push(byteArray)
  }

  var blob = new Blob(byteArrays, { type: contentType })
  return blob
}

const isProd = () => {
  return window.location.host === 'admin.blocktickets.xyz'
}

const isPreview = () => {
  return window.location.host === 'admin.preview.blocktickets.xyz'
}

const isDevelopment = () => {
  return window.location.host === 'admin.development.blocktickets.xyz'
}

const isLocal = () => {
  return window.location.host.includes('localhost')
}

export const timezones = (abbr) => {
  let zones = [
    {
      value: 'Eastern Daylight Time',
      abbr: 'EDT',
      offset: -4,
      isdst: true,
      text: '(UTC-04:00) Eastern Daylight Time (US & Canada)',
      utc: [
        'America/Detroit',
        'America/Havana',
        'America/Indiana/Petersburg',
        'America/Indiana/Vincennes',
        'America/Indiana/Winamac',
        'America/Iqaluit',
        'America/Kentucky/Monticello',
        'America/Louisville',
        'America/Montreal',
        'America/Nassau',
        'America/New_York',
        'America/Nipigon',
        'America/Pangnirtung',
        'America/Port-au-Prince',
        'America/Thunder_Bay',
        'America/Toronto',
      ],
      stateAbbreviation: [
        'me',
        'ny',
        'pa',
        'mi',
        'in',
        'oh',
        'wv',
        'va',
        'nc',
        'sc',
        'ga',
        'fl',
        'nj',
        'tn',
        'md',
        'ri',
        'ma',
      ],
    },
    {
      value: 'Central Daylight Time',
      abbr: 'CDT',
      offset: -5,
      isdst: true,
      text: '(UTC-06:00) Central Time (US & Canada)',
      utc: [
        'America/Chicago',
        'America/Texas',
        'America/Louisiana',
        'America/Indiana/Knox',
        'America/Indiana/Tell_City',
        'America/Matamoros',
        'America/Menominee',
        'America/North_Dakota/Beulah',
        'America/North_Dakota/Center',
        'America/North_Dakota/New_Salem',
        'America/Rainy_River',
        'America/Rankin_Inlet',
        'America/Resolute',
        'America/Winnipeg',
        'CST6CDT',
      ],
      stateAbbreviation: [
        'tx',
        'la',
        'ar',
        'ms',
        'al',
        'ok',
        'ks',
        'mo',
        'il',
        'wi',
        'mn',
        'nd',
        'sd',
        'ne',
        'ia',
        'ky',
      ],
    },
    {
      value: 'Mountain Daylight Time',
      abbr: 'MDT',
      offset: -6,
      isdst: true,
      text: '(UTC-07:00) Mountain Time (US & Canada)',
      utc: [
        'America/Boise',
        'America/Cambridge_Bay',
        'America/Denver',
        'America/Edmonton',
        'America/Inuvik',
        'America/Ojinaga',
        'America/Yellowknife',
        'MST7MDT',
      ],
      stateAbbreviation: ['mt', 'id', 'wy', 'ut', 'co', 'az', 'nm'],
    },
    {
      value: 'Pacific Standard Time',
      abbr: 'PST',
      offset: -8,
      isdst: false,
      text: '(UTC-08:00) Pacific Standard Time (US & Canada)',
      utc: [
        'America/Los_Angeles',
        'America/Tijuana',
        'America/Vancouver',
        'PST8PDT',
      ],
      stateAbbreviation: ['wa', 'or', 'ca', 'nv'],
    },
  ]

  let offset = zones.find((zone) => zone.abbr === abbr)

  return offset
}

export const getPreviewImage = (venue) => {
  const bucket = `https://blocktickets.nyc3.cdn.digitaloceanspaces.com`
  let imgURL = `${bucket}/venues/${venue}/preview/preview.png`
  return imgURL
}

export const truncateToTwoDecimals = (num) => {
  const n = Number(num)
  if (!Number.isFinite(n)) return 0
  // `n * 100` can land just below an exact cent (2098.43 * 100 is
  // 209842.99999999997), so flooring the raw product drops a penny. Snap the
  // scaled value back to the nearest 1e-6 first, which is far finer than any
  // real sub-cent amount we truncate but coarser than the representation error.
  return Math.floor(Math.round(n * 100 * 1e6) / 1e6) / 100
}

const resolveEventFeeStructure = (event) =>
  event?.fee_structure || event?.organization?.fee_structure || null

// Orgs that keep the legacy $1/ticket group (20+) assignment service fee
// instead of organization_fee_config price tiers.
const GROUP_ASSIGNMENT_ONE_DOLLAR_ORG_UUIDS = new Set([
  '001ba6b5-0595-4133-8ec9-1fa29df4154c',
  'a9022e37-a64d-4f4d-bd5d-31a935526d06',
])

const getEventOrganizationUUID = (event) =>
  event?.organizationUUID || event?.organization?.uuid || null

const calculateServiceFee = (ticketsType, event) => {
  // Schema default is true; treat missing as enabled so lean event payloads
  // that omit the flag still charge the configured assignment service fee.
  if (event?.includeServiceFees === false) return 0

  const feeStructure = resolveEventFeeStructure(event)
  let total
  switch (ticketsType) {
    case 'comp':
      total = 0
      break
    case 'individual':
      total = feeStructure?.groupPurchaseIndividuals || 0
      break
    case 'group':
      total = feeStructure?.groupPurchaseGroup || 0
      break
    default:
      total = 0
      break
  }
  return total
}

// Select per-ticket service fee from organization_fee_config rules.
// Rules are expected to be in the OrganizationFeeConfig format (shared-utils),
// but we keep this tolerant to partial rule objects as well.
const selectOrgConfigServiceFeePerTicket = (
  organizationFeeConfig,
  seatCount,
  pricePerTicket,
  feeContext = {}
) => {
  const rules =
    organizationFeeConfig?.rules ||
    organizationFeeConfig?.attributes?.rules ||
    organizationFeeConfig?.data?.rules ||
    []

  if (!Array.isArray(rules) || rules.length === 0) return null

  const qty = Number.parseInt(seatCount, 10)
  if (!Number.isFinite(qty) || qty <= 0) return 0

  const unitPrice = Number.parseFloat(pricePerTicket)
  const hasPrice = Number.isFinite(unitPrice) && unitPrice >= 0

  const sourceValue = feeContext?.source || 'ticket_assignment'
  const SOURCE = String(sourceValue).toLowerCase()
  const categoryValue = feeContext?.category || 'service'
  const CATEGORY = String(categoryValue).toLowerCase()
  const PRODUCT_TYPE = feeContext?.productType
    ? String(feeContext.productType).toLowerCase()
    : undefined

  // Prefer rules scoped to productType when provided; if none match, fall back.
  const hasProductScoped =
    PRODUCT_TYPE &&
    rules.some(
      (r) =>
        r?.productType !== undefined &&
        String(r?.productType || '').toLowerCase() === PRODUCT_TYPE
    )

  const productScoped = hasProductScoped
    ? rules.filter(
        (r) => String(r?.productType || '').toLowerCase() === PRODUCT_TYPE
      )
    : rules

  // Prefer rules explicitly scoped to source; if none exist, fall back
  // to any rules (to support orgs that use "global" rules without a source).
  const hasSourceScoped = productScoped.some(
    (r) => String(r?.source || '').toLowerCase() === SOURCE
  )

  const scoped = (
    hasSourceScoped
      ? productScoped.filter(
          (r) => String(r?.source || '').toLowerCase() === SOURCE
        )
      : productScoped
  ).filter((r) => {
    // If category is present, it must match requested category.
    // If category is missing, assume the rule is intended for service fee.
    const cat = r?.category
    if (cat === undefined || cat === null || cat === '') return true
    return String(cat).toLowerCase() === CATEGORY
  })

  // Prefer source-scoped price tiers when present; otherwise fall back to quantity tiers.
  const withMax = scoped
    .map((r) => ({
      rule: r,
      maxQuantity:
        r?.maxQuantity === null || r?.maxQuantity === undefined
          ? null
          : Number.parseInt(r.maxQuantity, 10),
      maxPrice:
        r?.maxPrice === null || r?.maxPrice === undefined
          ? null
          : Number.parseFloat(r.maxPrice),
      value:
        r?.value !== undefined
          ? Number.parseFloat(r.value)
          : r?.fee !== undefined
            ? Number.parseFloat(r.fee)
            : NaN,
    }))
    .filter((x) => Number.isFinite(x.value))

  const priceSourceRules = withMax.filter(
    (x) =>
      x.rule?.maxPrice !== undefined &&
      (x.maxQuantity === null ||
        !Number.isFinite(x.maxQuantity) ||
        qty <= x.maxQuantity) &&
      (String(x.rule?.source || '').toLowerCase() === SOURCE ||
        x.rule?.source === undefined ||
        x.rule?.source === null ||
        x.rule?.source === '')
  )

  if (hasPrice && priceSourceRules.length > 0) {
    const priceTiers = priceSourceRules
      .filter((x) => Number.isFinite(x.maxPrice))
      .sort((a, b) => a.maxPrice - b.maxPrice)

    const matchedPriceTier = priceTiers.find((t) => unitPrice <= t.maxPrice)
    if (matchedPriceTier) return matchedPriceTier.value

    const priceFallback = priceSourceRules.find((x) => x.maxPrice === null)
    if (priceFallback) return priceFallback.value
  }

  const qtyTiers = withMax
    .filter((x) => Number.isFinite(x.maxQuantity))
    .sort((a, b) => a.maxQuantity - b.maxQuantity)

  if (qtyTiers.length > 0) {
    const matchedQtyTier = qtyTiers.find((t) => qty <= t.maxQuantity)
    if (matchedQtyTier) return matchedQtyTier.value

    const maxTier = qtyTiers[qtyTiers.length - 1]
    if (qty > maxTier.maxQuantity) {
      const qtyFallback = withMax.find((x) => x.maxQuantity === null)
      if (qtyFallback) return qtyFallback.value
    }
  } else {
    const qtyFallback = withMax.find((x) => x.maxQuantity === null)
    if (qtyFallback) return qtyFallback.value
  }

  return 0
}

export const groupPurchasePricing = (
  amount,
  event,
  ticketCount,
  ticketType,
  paymentMethod,
  organizationFeeConfig
) => {
  // Early validation
  if (!event) {
    console.warn('Event not provided - cannot get tax rates')
    return null
  }

  // Calculate base pricing
  const pricePerTicket =
    ticketType === 'comp'
      ? 0
      : Number.isFinite(Number.parseFloat(amount))
        ? Number.parseFloat(amount)
        : 0
  const totalAmount = pricePerTicket * ticketCount

  // Calculate sales tax if applicable
  const taxRate = Number(event?.taxRate?.estimatedCombinedRate) || 0
  const salesTax = event?.includeTaxes
    ? Number(parseFloat(totalAmount * taxRate).toFixed(2))
    : 0

  const purchasePriceWithTax = event?.includeTaxes
    ? totalAmount + salesTax
    : totalAmount

  // Calculate service fees if applicable.
  // Prefer organization_fee_config. Individual tiers are capped at 20 seats;
  // a group assignment (20+ seats, or a group buyer type) uses the group tier
  // even when fewer seats are selected. Fall back to the event's legacy
  // fee_structure (groupPurchaseIndividuals / groupPurchaseGroup) only when
  // no org fee rules are configured.
  // Exception: selected orgs keep a flat $1/ticket group rate.
  const organizationUUID = getEventOrganizationUUID(event)
  const usesOneDollarGroupFee =
    ticketType === 'group' &&
    event?.includeServiceFees !== false &&
    organizationUUID &&
    GROUP_ASSIGNMENT_ONE_DOLLAR_ORG_UUIDS.has(organizationUUID)
  // Individual org rules use maxQuantity 20. A group assignment must clear
  // that cap so the open-ended group rule is selected.
  const serviceFeeQuantity =
    ticketType === 'group'
      ? Math.max(Number(ticketCount) || 0, 21)
      : ticketCount

  let serviceFeePerTicket
  if (ticketType === 'comp') {
    serviceFeePerTicket = 0
  } else if (usesOneDollarGroupFee) {
    serviceFeePerTicket = 1
  } else {
    const serviceFeePerTicketFromConfig = selectOrgConfigServiceFeePerTicket(
      organizationFeeConfig,
      serviceFeeQuantity,
      pricePerTicket
    )
    serviceFeePerTicket =
      serviceFeePerTicketFromConfig !== null
        ? serviceFeePerTicketFromConfig
        : calculateServiceFee(ticketType, event) || 0
  }

  const serviceFee = (serviceFeePerTicket || 0) * (ticketCount || 0)

  // Global per-ticket facility fee from Event Settings. Website pricing applies it in
  // utility.calculatePricingObjectPrice, so assignments must collect it too or assigned
  // seats under-collect the venue fee. Comps / $0 assignments stay free.
  const facilityFeePerTicket =
    pricePerTicket > 0 && event?.includeFees !== false
      ? Number.parseFloat(event?.facilityFees) || 0
      : 0
  const ticketFacilityFee =
    Math.round(facilityFeePerTicket * (ticketCount || 0) * 100) / 100

  // Calculate subtotal before processing fees
  const priceWithoutProcessing =
    purchasePriceWithTax + serviceFee + ticketFacilityFee

  // Assignment pricing should match standard Stripe estimate:
  // fee = amount * 2.9% + $0.30 (rounded to cents).
  // Use subtotal-before-processing as the amount basis.
  let paymentProcessingFee = getProcessingFeeFromChargeAmount(
    paymentMethod,
    priceWithoutProcessing
  )

  // Calculate final total
  const purchasePriceWithTaxAndFees =
    priceWithoutProcessing + paymentProcessingFee

  // Return calculated values with both raw numbers and formatted currency
  return {
    // Basic pricing
    pricePerTicket,
    ticketCount,
    totalAmount,

    // Tax calculations
    taxRate,
    salesTax,
    salesTaxPerTicket: ticketCount > 0 ? salesTax / ticketCount : 0,

    // Service fee calculations
    serviceFee,
    serviceFeePerTicket: ticketCount > 0 ? serviceFee / ticketCount : 0,

    // Facility fee calculations
    ticketFacilityFee,
    ticketFacilityFeePerTicket: facilityFeePerTicket,

    // Payment processing fee calculations
    paymentProcessingFee,
    paymentProcessingFeePerTicket: truncateToTwoDecimals(
      ticketCount > 0 ? paymentProcessingFee / ticketCount : 0
    ),

    // Final totals
    purchasePriceWithTax,
    purchasePriceWithTaxAndFees: truncateToTwoDecimals(
      purchasePriceWithTaxAndFees
    ),

    // Payout amount (what the organizer receives)
    yourPayout: totalAmount + ticketFacilityFee,
  }
}

export const packagePricing = (
  eventPackage,
  ticketCount,
  paymentMethod,
  multiplyAdditionalFees = true,
  pricePerTicketOverride,
  organization,
  organizationFeeConfig,
  feeContext
) => {
  if (!eventPackage) {
    console.warn('Package not provided cant get tax rates')
    return
  }

  // Calculate base pricing
  const override =
    pricePerTicketOverride !== undefined &&
    pricePerTicketOverride !== null &&
    pricePerTicketOverride !== ''
      ? Number.parseFloat(pricePerTicketOverride)
      : undefined

  const basePricePerTicket = Number.parseFloat(eventPackage.price) || 0
  const pricePerTicket = Number.isFinite(override)
    ? override
    : basePricePerTicket
  const totalAmount = pricePerTicket * ticketCount

  // Calculate sales tax if applicable
  const taxRate = Number(eventPackage.estimatedCombinedRate) || 0
  const salesTax = organization?.taxOnSeasonPackages
    ? Number(parseFloat(totalAmount * taxRate).toFixed(2))
    : 0

  const purchasePriceWithTax = eventPackage.includeTax
    ? totalAmount + salesTax
    : totalAmount

  // Calculate additional fees
  const additionalFees = multiplyAdditionalFees
    ? (eventPackage.additionalFees || 0) * (eventPackage.events?.length || 1)
    : eventPackage.additionalFees || 0

  const flatPackageServiceFee = getPackageFlatServiceFee(eventPackage)
  const isSeasonPackageTicketAssignment = Boolean(
    feeContext?.source === ORDER_SOURCE.TICKET_ASSIGNMENT &&
    feeContext?.productType === PRODUCT_TYPE.SEASON_PACKAGES
  )
  const packageGameCount = Array.isArray(eventPackage.events)
    ? eventPackage.events.length
    : 0
  const serviceFeeQuantity =
    isSeasonPackageTicketAssignment && packageGameCount > 0
      ? (ticketCount || 0) * packageGameCount
      : ticketCount || 0
  const serviceFeePricingAmount =
    isSeasonPackageTicketAssignment && packageGameCount > 0
      ? pricePerTicket / packageGameCount
      : pricePerTicket
  const paymentMethodKey = String(paymentMethod || '').toUpperCase()
  const isComplimentaryPackageAssignment =
    paymentMethodKey === 'COMP' ||
    paymentMethodKey === 'COMPLIMENTARY' ||
    (Number.isFinite(pricePerTicket) && pricePerTicket <= 0)

  // Flat package override replaces org per-seat/per-game service fee rules.
  // Comp / $0 season tickets stay fee-free — $0 matches the lowest maxPrice
  // org tier (NMSU Comp Season) and would otherwise attach a Blocktickets fee.
  let serviceFee
  let serviceFeePerTicket
  let serviceFeeMode = 'per-seat'
  if (isComplimentaryPackageAssignment) {
    serviceFee = 0
    serviceFeePerTicket = 0
    serviceFeeMode = 'waived'
  } else if (flatPackageServiceFee !== null) {
    serviceFee = flatPackageServiceFee
    serviceFeePerTicket = ticketCount > 0 ? serviceFee / ticketCount : 0
    serviceFeeMode = 'package-flat'
  } else {
    // Ticket assignment service fee (org fee config)
    const serviceFeePerTicketFromConfig = selectOrgConfigServiceFeePerTicket(
      organizationFeeConfig,
      serviceFeeQuantity,
      serviceFeePricingAmount,
      feeContext
    )
    serviceFeePerTicket =
      serviceFeePerTicketFromConfig !== null ? serviceFeePerTicketFromConfig : 0
    serviceFee = (serviceFeePerTicket || 0) * serviceFeeQuantity
  }

  // Calculate subtotal before processing fees
  const priceWithoutProcessing =
    purchasePriceWithTax + additionalFees + serviceFee

  // fee = amount * 2.9% + $0.30 on subtotal-before-processing (matches backend utility)
  let paymentProcessingFee = getProcessingFeeFromChargeAmount(
    paymentMethod,
    priceWithoutProcessing
  )

  // Calculate final total
  const finalTotal = priceWithoutProcessing + paymentProcessingFee

  // Return comprehensive pricing breakdown
  return {
    // Service fees
    serviceFeePerTicket:
      serviceFeeMode === 'package-flat'
        ? serviceFeePerTicket
        : ticketCount > 0
          ? serviceFee / ticketCount
          : 0,
    serviceFee: serviceFee,
    serviceFeeMode,
    isFlatServiceFee: serviceFeeMode === 'package-flat',

    // Base pricing
    pricePerTicket,
    ticketCount,
    totalAmount,

    // Tax information
    salesTax: salesTax,
    salesTaxPerTicket: ticketCount > 0 ? salesTax / ticketCount : 0,
    taxRate,

    // Additional fees
    additionalFees: additionalFees,
    additionalFeesPerTicket: ticketCount > 0 ? additionalFees / ticketCount : 0,

    // Processing fees
    paymentProcessingFee,
    paymentProcessingFeePerTicket: truncateToTwoDecimals(
      ticketCount > 0 ? paymentProcessingFee / ticketCount : 0
    ),

    // Totals
    purchasePriceWithTax,
    purchasePriceWithTaxAndFees: truncateToTwoDecimals(finalTotal),
    yourPayout: Number(parseFloat(totalAmount)),
  }
}

export const getProcessingFee = (paymentMethod, price) => {
  // Same formula as backend utility.getProcessingFee: $0.30 + (amount * 2.9%).
  return getProcessingFeeFromChargeAmount(paymentMethod, price)
}

export const getProcessingFeeFromChargeAmount = (
  paymentMethod,
  chargeAmount
) => {
  const method = String(paymentMethod || '').toUpperCase()
  const CARD_LIKE = new Set([
    'CARD',
    'INVOICE',
    'POS',
    'CREDIT-CARD',
    'CREDIT_CARD',
    'POS-TERMINAL',
    'POS_TERMINAL',
  ])

  const amount = Number(chargeAmount)
  if (!CARD_LIKE.has(method) || !Number.isFinite(amount) || amount <= 0) {
    return 0
  }

  const fixed = 0.3
  const pct = 0.029
  return Math.round((fixed + amount * pct) * 100) / 100
}

/**
 * Returns a human-readable payment method label for receipts/invoices.
 * @param {Object} order - Order with intentDetails, paymentStatus, paymentType, paymentMethodType
 * @returns {string}
 */
const normalizeReceiptPaymentKey = (value) => {
  const key = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  return key === 'transfer' ? 'e_transfer' : key
}

const RECEIPT_PAYMENT_LABELS = {
  cash: 'Cash',
  e_transfer: 'E-transfer',
  check: 'Check',
  team_credit: 'Team credit',
  external: 'External',
  payroll_deduction: 'Payroll deduction',
  plan: 'Payment plan',
  released: 'Complimentary',
  draft: 'Draft',
}

export const getPaymentMethodLabelForReceipt = (order) => {
  if (!order) return '—'
  const card =
    order?.intentDetails?.charges?.data?.[0]?.payment_method_details?.card
  if (card?.brand && card?.last4) {
    return `${capitalizeString(card.brand)} •••• ${card.last4}`
  }
  const statusKey = normalizeReceiptPaymentKey(order.paymentStatus)
  const typeKey = normalizeReceiptPaymentKey(order.paymentType)
  const methodKey = normalizeReceiptPaymentKey(
    order.paymentMethodType || order.paymentOption
  )
  const methodType = String(
    order.paymentMethodType || order.paymentOption || ''
  ).trim()
  const recordedVoucher = methodKey === 'voucher' || typeKey === 'voucher'

  // "cash" is also the fallback when e-transfer was missing from the status map,
  // and "paid" does not name the tender. Settlement uses paymentMethodType.
  const genericStatus =
    !statusKey ||
    statusKey === 'cash' ||
    statusKey === 'paid' ||
    statusKey === 'open'
  if (genericStatus && methodKey !== 'cash') {
    if (RECEIPT_PAYMENT_LABELS[methodKey])
      return RECEIPT_PAYMENT_LABELS[methodKey]
    if (RECEIPT_PAYMENT_LABELS[typeKey] && typeKey !== 'cash') {
      return RECEIPT_PAYMENT_LABELS[typeKey]
    }
  }

  if (RECEIPT_PAYMENT_LABELS[statusKey] && !recordedVoucher) {
    return RECEIPT_PAYMENT_LABELS[statusKey]
  }
  if (typeKey === 'card' || typeKey === 'credit_card')
    return 'Credit/Debit Card'
  if (typeKey === 'cash') return 'Cash'
  if (typeKey === 'payment_plan') return 'Payment plan'
  if (methodType) return capitalizeString(methodType.replace(/_/g, ' '))
  if (typeKey) return capitalizeString(typeKey.replace(/_/g, ' '))
  if (statusKey && statusKey !== 'open' && statusKey !== 'paid') {
    return capitalizeString(statusKey.replace(/_/g, ' '))
  }
  return '—'
}

export const truncate = (text, maxLength) => {
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
}

export const getValidQuantitiesForTicketGroup = (
  offer,
  availableSeatsLength,
  eventTicketLimit
) => {
  const limit = offer.limit
  const minQuantity = offer.minQuantity || 0
  const maxQuantity = Math.min(offer.maxQuantity || 100, 100)
  const multipleOf = offer.multipleOf || 1

  // If limit is set (and not 0), return just that value
  if (limit > 0) {
    return limit <= availableSeatsLength ? [limit] : []
  }

  // Calculate the effective maximum (considering eventTicketLimit and availableSeats)
  const effectiveMax = Math.min(
    maxQuantity,
    eventTicketLimit || 100,
    availableSeatsLength
  )

  // Generate array of valid quantities
  const quantities = []
  for (let i = minQuantity; i <= effectiveMax; i++) {
    if (i % multipleOf === 0) {
      quantities.push(i)
    }
  }

  return quantities
}

export const getDefaultSeason = (seasons) => {
  // select the current season - start and end time is in between current time
  return seasons?.find((season) => {
    const timezone = timezones(season?.venue?.timezone)
    const now = getNowInTimezone(timezone)
    const startTime = getTimezoneDate(season.start, timezone)
    const endTime = getTimezoneDate(season.ends, timezone)
    // Check if now is between start and end ([] = inclusive - includes start and end time)
    return now.isBetween(startTime, endTime, null, '[]')
  })
}

export const labelize = (key) => {
  return key
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export const getPaymentOption = (option) => {
  return onlinePaymentMethods.includes(option) ? 'online' : 'in-person'
}

export const inPersonPaymentMethods = [
  'CASH',
  'CHECK',
  'TEAM_CREDIT',
  'EXTERNAL',
  'PAYROLL_DEDUCTION',
  'E_TRANSFER',
  'COMP',
  'RESERVED',
]

export const getVisibleInPersonPaymentMethods = (organizationUUID) =>
  inPersonPaymentMethods.filter(
    (method) =>
      method !== 'PAYROLL_DEDUCTION' ||
      isPayrollDeductionEnabled(organizationUUID)
  )

export const onlinePaymentMethods = ['CARD', 'INVOICE']

export const CARD_LIKE_ASSIGNMENT_PAYMENT_METHODS = new Set([
  'CARD',
  'INVOICE',
  'POS',
])

export const isCardLikeAssignmentPaymentMethod = (paymentMethod) =>
  CARD_LIKE_ASSIGNMENT_PAYMENT_METHODS.has(
    String(paymentMethod || '').toUpperCase()
  )

export const getAssignmentPaymentMethodKeys = (
  organizationUUID,
  posTerminalProfile
) => {
  const methods = onlinePaymentMethods.concat(
    getVisibleInPersonPaymentMethods(organizationUUID)
  )
  if (posTerminalProfile?.location && !methods.includes('POS')) {
    const invoiceIndex = methods.indexOf('INVOICE')
    if (invoiceIndex >= 0) {
      methods.splice(invoiceIndex + 1, 0, 'POS')
    } else {
      methods.push('POS')
    }
  }
  return methods
}

export const getAssignmentPaymentMethodLabel = (key) =>
  key === 'POS' ? 'Terminal' : labelize(key)

export const getAssignmentPaymentAmountDescription = (paymentMethod) => {
  if (paymentMethod === 'CARD') return 'charge on card'
  if (paymentMethod === 'POS') return 'charge on terminal'
  return `collect via ${formatString(paymentMethod)} from buyer`
}

export const getAmountDue = (
  pricingObject,
  paymentMethod,
  hasPaymentPlan,
  paymentPlans
) => {
  const method = String(paymentMethod || '').toUpperCase()
  const CARD_LIKE = new Set(['CARD', 'INVOICE', 'POS'])
  const IN_PERSON_WITH_SERVICE_FEE = new Set([
    'CASH',
    'CHECK',
    'TEAM_CREDIT',
    'EXTERNAL',
    'PAYROLL_DEDUCTION',
    'E_TRANSFER',
  ])

  let buyerPrice

  // Card-like totals already embed this in purchasePriceWithTaxAndFees.
  const facilityFee = pricingObject?.ticketFacilityFee ?? 0

  if (method === 'COMP' || method === 'RESERVED') {
    buyerPrice = 0
  } else if (CARD_LIKE.has(method)) {
    buyerPrice = pricingObject?.purchasePriceWithTaxAndFees ?? 0
  } else if (IN_PERSON_WITH_SERVICE_FEE.has(method)) {
    const purchasePriceWithTax = pricingObject?.purchasePriceWithTax ?? 0
    const serviceFee = pricingObject?.serviceFee ?? 0
    buyerPrice = purchasePriceWithTax + serviceFee + facilityFee
  } else {
    const purchasePriceWithTax = pricingObject?.purchasePriceWithTax ?? 0
    const serviceFee = pricingObject?.serviceFee ?? 0
    buyerPrice = purchasePriceWithTax + serviceFee + facilityFee
  }

  // payment plan override (highest priority)
  if (hasPaymentPlan) {
    buyerPrice = Object.values(paymentPlans)[0]?.amount ?? buyerPrice
  }

  return buyerPrice
}

export {
  getOrderSeatLocationTickets,
  getOrderTicketQuantity,
  isPackageOrder,
} from './orderSeatLocation'
